import { clsx } from 'clsx';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useFieldControl } from './Field';

/** Shared control chrome so inputs, selects and textareas line up. */
export const CONTROL_CLASS =
  'w-full rounded-md border border-slate-300 bg-surface text-[13px] text-slate-900 shadow-xs transition-colors placeholder:text-slate-400 hover:border-slate-400 focus:border-ember-500 focus:outline-none focus:ring-2 focus:ring-ember-500/20 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 aria-invalid:border-red-400 aria-invalid:focus:ring-red-500/20';

export type InputSize = 'sm' | 'md';

export type InputProps = Omit<ComponentProps<'input'>, 'size'> & {
  size?: InputSize;
  /** Leading decorative icon (e.g. Search). */
  icon?: LucideIcon;
};

/** Text-like input. Pass `ref` directly (React 19). Inside `<Field>` it inherits id/aria wiring. */
export function Input({ size = 'md', icon: Icon, className, ...rest }: InputProps) {
  const control = useFieldControl(rest);
  const input = (
    <input
      {...rest}
      {...control}
      className={clsx(
        // An explicit width from the caller (e.g. "w-20") must win over the default full width.
        !Icon && className && /(^|\s)w-/.test(className) ? CONTROL_CLASS.replace(/^w-full /, '') : CONTROL_CLASS,
        size === 'sm' ? 'h-7 px-2' : 'h-8 px-2.5',
        Icon && 'pl-8',
        !Icon && className,
      )}
    />
  );
  if (!Icon) return input;
  return (
    <div className={clsx('relative', className)}>
      <Icon size={15} aria-hidden className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-slate-400" />
      {input}
    </div>
  );
}

export type TextareaProps = ComponentProps<'textarea'>;

export function Textarea({ className, rows = 4, ...rest }: TextareaProps) {
  const control = useFieldControl(rest);
  return <textarea rows={rows} {...rest} {...control} className={clsx(CONTROL_CLASS, 'px-2.5 py-2 leading-5', className)} />;
}

export type SelectOption = { value: string; label: string; disabled?: boolean };

export type SelectProps = Omit<ComponentProps<'select'>, 'size'> & {
  size?: InputSize;
  /** Options to render; alternatively pass `<option>` children. */
  options?: readonly SelectOption[];
  /** Adds a first empty option with this label (value ""). */
  placeholder?: string;
};

/** Native select styled like the other controls. */
export function Select({ size = 'md', options, placeholder, className, children, ...rest }: SelectProps) {
  const control = useFieldControl(rest);
  return (
    <div className={clsx('relative', className)}>
      <select
        {...rest}
        {...control}
        className={clsx(CONTROL_CLASS, 'appearance-none pr-8', size === 'sm' ? 'h-7 pl-2' : 'h-8 pl-2.5')}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options?.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
        {children}
      </select>
      <ChevronDown
        size={14}
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-slate-400"
      />
    </div>
  );
}
